import {
  ButtonType,
  CaesarsButton,
  MFAChannel,
  PreferenceSelectorState,
  Selectors,
  openURL,
  useIsDesktop,
  useIsLargeDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {Pressable, Text, View} from 'native-base';
import React from 'react';
import {Platform} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';
import {useTheme} from 'styled-components/native';
import {webViewQueryParam} from 'utils';
import {isWebViewSelector} from 'store/content';
import {useSelector} from 'Provider';

interface VerificationMethodDisplayProps {
  isSelected: boolean;
  verificationMethod: MFAChannel;
  isPreferredChannel: boolean;
  contactInfo?: string;
  onPressed: () => void;
  editing: boolean;
  isVerified: boolean;
  setMFAWebViewUrl: (url: string) => void;
}

const VerificationMethodDisplay: React.FC<
  VerificationMethodDisplayProps
> = props => {
  const {
    isSelected,
    isPreferredChannel,
    verificationMethod,
    contactInfo,
    onPressed,
    editing,
    isVerified,
    setMFAWebViewUrl,
  } = props;

  const isLargeDesktop = useIsLargeDesktop();
  const isDesktop = useIsDesktop();
  const theme = useTheme();
  const {tuna, errorLight, caesarRewards, confirm, error} = theme.colors;
  const isPhone = verificationMethod === MFAChannel.SMS;

  const contactInfoIcon = (
    <View
      backgroundColor={errorLight}
      height="50px"
      width="50px"
      justifyContent="center"
      alignItems="center"
      borderRadius="25px">
      <Icon
        name={isPhone ? 'mobile-alt' : 'envelope'}
        color={caesarRewards}
        size={21}
        solid={isPhone}
      />
    </View>
  );

  const mfaAddContactUrl = `/a/verification/verify-identity?action=${
    contactInfo
      ? isPhone
        ? PreferenceSelectorState.EditPhone
        : PreferenceSelectorState.EditEmail
      : isPhone
        ? PreferenceSelectorState.AddPhone
        : PreferenceSelectorState.AddEmail
  }`;

  const mfaWebViewUrl = `${mfaAddContactUrl}&webviewauth=true`;
  const isWeb = Platform.OS === 'web';
  const isWebView = useSelector(isWebViewSelector);
  let mfaWebUrl = `${mfaAddContactUrl}&forwardUrl=/rewards/profile%3Ftab%3Dsecurity`;
  if (isWebView) {
    mfaWebUrl += '&' + webViewQueryParam;
  }

  const onAddEditPress = () =>
    isWeb ? openURL(mfaWebUrl) : setMFAWebViewUrl(mfaWebViewUrl);

  if (!editing && !contactInfo) {
    return null;
  }

  const renderSecondaryButton = (
    <CaesarsButton
      title={isPhone ? '+ Add Phone' : '+ Add Email'}
      type={ButtonType.SECONDARY}
      onPress={onAddEditPress}
    />
  );

  const renderContactTitle = (
    <Text
      fontFamily="body"
      fontWeight={600}
      mb={1}
      fontSize={3}
      lineHeight="19px">
      {isPhone ? 'Text message' : 'Email message'}
    </Text>
  );

  const checkmarkIcon =
    (isSelected && editing) || (isPreferredChannel && !editing) ? (
      <Icon name="check-circle" color={confirm} solid size={34} />
    ) : (
      editing && (
        <View
          borderColor="#D8D8D8"
          borderWidth="2px"
          height="34px"
          width="34px"
          borderRadius="25px"
        />
      )
    );

  const renderContactLabel = (
    <Text mb={1} fontFamily="body" fontSize={1} lineHeight="15px" color={tuna}>
      {isPhone ? 'Phone number' : 'Email address'}:{'  '}
    </Text>
  );

  const renderContactInfo = contactInfo ? (
    <Text
      fontFamily="body"
      mr={4}
      lineHeight="15px"
      fontWeight={600}
      fontSize={1}
      color={tuna}>
      {contactInfo}
    </Text>
  ) : null;

  const renderIsVerifiedLabel = (
    <View flexDirection="row" mb={1}>
      <Icon
        name={isVerified ? 'check-circle' : 'times-circle'}
        color={isVerified ? confirm : error}
        solid
        size={11}
      />
      <Text
        ml={1}
        color="paleSky"
        fontFamily="body"
        lineHeight="12px"
        fontSize={1}>
        {isVerified ? 'Verified' : 'Unverified'}
      </Text>
    </View>
  );

  const renderCodeInstructionDetails = contactInfo ? (
    <Text fontFamily="body" lineHeight="11px" fontSize="10px" color={tuna}>
      Verification codes are sent by {isPhone ? 'SMS' : 'email'}
    </Text>
  ) : (
    <Text
      fontFamily="body"
      lineHeight="15px"
      fontSize="11px"
      fontWeight={600}
      color={tuna}>
      No {isPhone ? 'phone number' : 'email address'}? Let&apos;s add it.
    </Text>
  );

  const renderEditButton = (
    <Pressable
      onPress={onAddEditPress}
      height="25px"
      width="50px"
      mr={5}
      alignItems="center"
      flexDirection="row">
      <Icon name="pencil" color={tuna} solid size={11} light />
      <Text
        color={tuna}
        fontSize={1}
        fontFamily="body"
        fontWeight={600}
        lineHeight="20px"
        ml={2}>
        Edit
      </Text>
    </Pressable>
  );

  const renderVerficationDetails = () => {
    return isDesktop ? (
      <>
        <View flexDirection="row">
          {contactInfoIcon}
          <View flexDirection="column" pl={5} flex={1}>
            {renderContactTitle}
            <View flexDirection="row" mb={1} justifyContent="center">
              {renderContactLabel}
              {!!contactInfo && renderContactInfo}
              {contactInfo
                ? renderIsVerifiedLabel
                : renderCodeInstructionDetails}
            </View>
            {contactInfo && renderCodeInstructionDetails}
          </View>
        </View>
        {editing && !contactInfo ? (
          renderSecondaryButton
        ) : (
          <View justifyContent="center" flexDirection="row" alignItems="center">
            {editing && renderEditButton}
            {checkmarkIcon}
          </View>
        )}
      </>
    ) : (
      <>
        <View flexDirection="row" width="100%">
          {contactInfoIcon}
          <View flexDirection="column" pl={4} flex={1}>
            <View flexDirection="row" justifyContent="space-between">
              <View flexDirection="column">
                {renderContactTitle}
                {renderContactLabel}
              </View>
              {!!contactInfo && <View>{checkmarkIcon}</View>}
            </View>
            <View flexDirection="column" mb={1}>
              {renderContactInfo}
              {!!contactInfo && (
                <>
                  <View height={1} width="100%" />
                  {renderIsVerifiedLabel}
                </>
              )}
            </View>
            {renderCodeInstructionDetails}
            {editing &&
              (contactInfo ? (
                <View mt="18px">{renderEditButton}</View>
              ) : (
                <View mt="18px" alignSelf="flex-start">
                  {renderSecondaryButton}
                </View>
              ))}
          </View>
        </View>
      </>
    );
  };

  /**
   * Editing
   */
  if (editing) {
    return (
      <Pressable
        _hover={editing ? {shadow: 2} : undefined}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        cursor="pointer"
        width={isLargeDesktop ? '874px' : '100%'}
        onPress={onPressed}
        minHeight={isDesktop ? '104px' : '139px'}
        borderColor={isSelected ? tuna : '#DDD'}
        borderWidth={isSelected ? '2px' : '1px'}
        justifyContent="space-between"
        alignItems="center"
        borderRadius="8px"
        p={5}
        flexDirection="row">
        {renderVerficationDetails()}
      </Pressable>
    );
  }

  /**
   * Read-only
   */
  return (
    <View
      width={isLargeDesktop ? '874px' : '100%'}
      minHeight={isDesktop ? '104px' : '139px'}
      borderColor={isSelected ? tuna : '#DDD'}
      borderWidth={isSelected ? '2px' : '1px'}
      justifyContent="space-between"
      alignItems="center"
      borderRadius="8px"
      p={5}
      flexDirection="row">
      {renderVerficationDetails()}
    </View>
  );
};

export default VerificationMethodDisplay;
